import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import {
  Card,
  CardBody,
  Button,
  Badge,
  Input,
  Label,
  FormGroup,
  Collapse,
  Row,
  Alert,
} from "reactstrap";
import Creatable from "../../../components/common/CreatableSelect";
import { Colxx } from "../../../components/common/CustomBootstrap";
import { AddCommaNumber } from "../../../helpers/Utils";
import PriceInput from "../../../components/common/PriceInput";

// Helper functions
const parseNumber = (str) => {
  if (!str) return "";
  let s = str.toString();
  // Standard VN format: 1.000,5 (If comma exists, assume it is decimal)
  if (s.includes(",")) {
    return s.replace(/\./g, "").replace(/,/g, ".");
  }
  // If only dots match strict thousand pattern (e.g. 1.000 or 1.000.000)
  // We assume it's an integer with thousand separators
  if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) {
    return s.replace(/\./g, "");
  }
  // Otherwise, treat dot as decimal (e.g. 0.2, 1.5, start with 0.)
  return s;
};

const formatNumber = (num) => {
  if (num === null || num === undefined || num === "") return "";

  // Chuyển về string
  let str = num.toString();

  // Tách phần nguyên và thập phân (input có thể là "0.5" hoặc "0,5")
  // Chuẩn hóa dấu phân cách thập phân đầu vào thành chấm
  str = str.replace(/,/g, ".");

  let parts = str.split(".");
  let integerPart = parts[0];
  let decimalPart = parts.length > 1 ? "," + parts[1] : "";

  // Format phần nguyên: thêm dấu chấm phân cách hàng nghìn
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  return integerPart + decimalPart;
};

// Helper cho các trường nhập kho (chỉ số nguyên, format đơn giản)
const removeDots = (str) => {
  if (!str) return "";
  return str.toString().replace(/\./g, "");
};

const RecipeBuilder = ({
  ingredients: propIngredients = [],
  data_stocks = [],
  data_providers = [],
  onChange,
  disabled = false,
  sellingPrice = 0,
  business_type = "",
  intl,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [localIngredients, setLocalIngredients] = useState(propIngredients);
  const [extraProviders, setExtraProviders] = useState([]);

  // Define unit placeholder based on business_type
  let unitPlaceholderId = "recipe.unit.default";
  if (business_type === "cafe" || business_type === "restaurant") {
    unitPlaceholderId = "recipe.unit.cafe";
  } else if (business_type === "fashion") {
    unitPlaceholderId = "recipe.unit.fashion";
  } else if (business_type === "cosmetics" || business_type === "beauty") {
    unitPlaceholderId = "recipe.unit.cosmetics";
  } else if (business_type === "retail" || business_type === "grocery") {
    unitPlaceholderId = "recipe.unit.retail";
  } else if (business_type === "pharmacy") {
    unitPlaceholderId = "recipe.unit.pharmacy";
  } else if (business_type === "electronics") {
    unitPlaceholderId = "recipe.unit.electronics";
  } else if (business_type === "spa" || business_type === "salon") {
    unitPlaceholderId = "recipe.unit.spa";
  }
  const unitPlaceholder = intl.formatMessage({ id: unitPlaceholderId });

  useEffect(() => {
    setLocalIngredients(propIngredients);
  }, [JSON.stringify(propIngredients)]);

  const updateIngredients = (newIngredients) => {
    setLocalIngredients(newIngredients);
    onChange && onChange(newIngredients);
  };

  const activeStocks = (data_stocks || []).filter(
    (stock) => stock.is_active !== false && stock.is_active !== "false",
  );

  const stockOptions = activeStocks.map((stock) => ({
    label: `${stock.name} (${stock.unit || intl.formatMessage({ id: "recipe.unit.generic" })})`,
    value: stock.id,
    name: stock.name,
    unit: stock.unit || intl.formatMessage({ id: "recipe.unit.generic" }),
    avarage_price: stock.avarage_price || 0,
  }));

  const checkIsStockInactive = (ingredient) => {
    if (!ingredient || !ingredient.stock_id) return false;
    const stock = (data_stocks || []).find((s) => s.id === ingredient.stock_id);
    return stock
      ? stock.is_active === false || stock.is_active === "false"
      : false;
  };

  // Combine props providers and locally created ones
  const allProviders = [
    ...data_providers,
    ...extraProviders.map((name) => ({ name })),
  ];
  // Remove duplicates based on name/value
  const uniqueProviders = [];
  const activeNames = new Set();
  allProviders.forEach((p) => {
    const name = p.name || p;
    if (!activeNames.has(name)) {
      activeNames.add(name);
      uniqueProviders.push({ label: name, value: name });
    }
  });
  const providerOptions = uniqueProviders;

  // Calculate Total Cost based on Ingredients
  const totalCost = localIngredients.reduce((sum, ing) => {
    const price = parseFloat(ing.price) || 0;
    const quantity = parseFloat(ing.quantity) || 0;
    return sum + price * quantity;
  }, 0);

  const handleAddIngredient = () => {
    const newIngredient = {
      id: `ing_${Date.now()}`,
      stock_id: null,
      stock_name: "",
      unit: "",
      quantity: "1",
      price: 0,
      is_new_stock: false,
    };
    updateIngredients([...localIngredients, newIngredient]);
  };

  const handleRemoveIngredient = (id) => {
    updateIngredients(localIngredients.filter((ing) => ing.id !== id));
  };

  const handleSelectStock = (option, ingredientId) => {
    const updatedIngredients = localIngredients.map((ing) => {
      if (ing.id === ingredientId) {
        if (option && option.__isNew__) {
          return {
            ...ing,
            stock_id: null,
            stock_name: option.label,
            is_new_stock: true,
            unit: "",
            price: 0,
            stock_quantity: "",
            stock_cost_price: "",
            stock_supplier: "",
            stock_paid: "",
          };
        } else if (option) {
          return {
            ...ing,
            stock_id: option.value,
            stock_name: option.name,
            unit: option.unit,
            price: option.avarage_price,
            is_new_stock: false,
          };
        }
      }
      return ing;
    });
    updateIngredients(updatedIngredients);
  };

  const handleQuantityInfoChange = (id, field, value) => {
    // [FIX] Sync newly created provider
    if (field === "stock_supplier" && value) {
      const str = value.toString().trim();
      const alreadyExists =
        data_providers.some((p) => (p.name || p) === str) ||
        extraProviders.includes(str);
      if (str && !alreadyExists) {
        setExtraProviders((prev) => [...prev, str]);
      }
    }

    let numValue = value;
    if (field !== "stock_supplier") {
      // Với trường quantity (SL sử dụng): dùng logic thông minh (hỗ trợ số lẻ)
      // Với trường stock_* (Nhập kho): dùng logic đơn giản (bỏ dấu chấm)
      if (field === "quantity") {
        numValue = parseNumber(value);
      } else {
        numValue = removeDots(value);
      }
    }

    const updatedIngredients = localIngredients.map((ing) => {
      if (ing.id === id) {
        const updates = { [field]: numValue };

        // If updating Cost Price, update the 'price' used for recipe calculation too
        if (field === "stock_cost_price") {
          updates.price = numValue;
        }

        return { ...ing, ...updates };
      }
      return ing;
    });
    updateIngredients(updatedIngredients);
  };

  const inactiveIngredientsCount = localIngredients.filter((ing) =>
    checkIsStockInactive(ing),
  ).length;

  // Render Logic
  const getSelectedStock = (ingredient) => {
    if (ingredient.stock_id) {
      const activeOpt = stockOptions.find(
        (opt) => opt.value === ingredient.stock_id,
      );
      if (activeOpt) return activeOpt;

      // Tìm trong toàn bộ kho nếu nguyên liệu đã bị ngừng kinh doanh hoặc ẩn
      const existingStock = (data_stocks || []).find(
        (s) => s.id === ingredient.stock_id,
      );
      if (existingStock) {
        return {
          value: existingStock.id,
          label: `${existingStock.name} (${existingStock.unit || "đơn vị"}) [Ngừng kinh doanh]`,
          name: existingStock.name,
          unit: existingStock.unit || "đơn vị",
          avarage_price: existingStock.avarage_price || 0,
          is_inactive: true,
        };
      }
    }
    if (ingredient.stock_name) {
      return {
        value: ingredient.stock_name,
        label: ingredient.is_new_stock
          ? ingredient.stock_name
          : `${ingredient.stock_name} (${ingredient.unit || "đơn vị"})`,
        __isNew__: ingredient.is_new_stock,
      };
    }
    return null;
  };

  const getSelectedProvider = (val) => {
    if (!val) return null;
    return { label: val, value: val };
  };

  return (
    <div className="recipe-builder mb-4">
      <div
        className="d-flex align-items-center justify-content-between mb-2 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="d-flex align-items-center">
          <i
            className="simple-icon-chemistry mr-2"
            style={{ fontSize: "18px" }}
          />
          <Label
            className="mb-0 font-weight-bold"
            style={{ cursor: "pointer" }}
          >
            {intl.formatMessage({ id: "recipe.title" })}
          </Label>
          {localIngredients.length > 0 && (
            <Badge color="primary" className="ml-2">
              {localIngredients.length}{" "}
              {intl.formatMessage({ id: "recipe.count-badge" })}
            </Badge>
          )}
        </div>
      </div>

      <Collapse isOpen={isOpen}>
        <Card className="border">
          <CardBody className="p-3 pt-5">
            {inactiveIngredientsCount > 0 && (
              <Alert
                color="warning"
                className="p-2 mb-3 text-small d-flex align-items-center"
              >
                <i
                  className="simple-icon-exclamation mr-2 font-weight-bold"
                  style={{ fontSize: "16px" }}
                />
                <div>
                  <strong>Lưu ý:</strong> Có {inactiveIngredientsCount} nguyên liệu trong định lượng đang ở trạng thái <strong>ngừng kinh doanh</strong>. Vui lòng chọn nguyên liệu thay thế hoặc xóa để đảm bảo xuất kho và tính giá vốn chính xác.
                </div>
              </Alert>
            )}
            {localIngredients.length === 0 ? (
              <p className="text-muted text-small text-center mb-3">
                {intl.formatMessage({ id: "recipe.empty-state" })}
              </p>
            ) : (
              localIngredients.map((ing, idx) => {
                const isInactive = checkIsStockInactive(ing);
                return (
                  <div
                    key={ing.id}
                    className={`ingredient-item mb-3 border-bottom ${
                      isInactive ? "bg-light-danger p-2 rounded" : ""
                    }`}
                  >
                    <Row className="align-items-start">
                      {/* HÀNG 1: Tên + SL sử dụng + Thành tiền + Xóa */}

                      {/* 1. Tên Nguyên Liệu (4) */}
                      <Colxx xxs="12" sm="5" md="6" className="has-float-label">
                        <FormGroup>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <Label className="text-muted text-small mb-0">
                              {intl.formatMessage({ id: "recipe.label.name" })}
                            </Label>
                            {isInactive && (
                              <Badge color="danger" pill className="text-extra-small">
                                Ngừng KD
                              </Badge>
                            )}
                          </div>
                          <Creatable
                            className="react-select"
                            classNamePrefix="react-select"
                            placeholder={intl.formatMessage({
                              id: "recipe.placeholder.select",
                            })}
                            options={stockOptions}
                            value={getSelectedStock(ing)}
                            onChange={(opt) => handleSelectStock(opt, ing.id)}
                            isDisabled={disabled}
                            formatCreateLabel={(val) =>
                              intl.formatMessage(
                                { id: "recipe.create-label" },
                                { val },
                              )
                            }
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                          {isInactive && (
                            <div className="text-danger text-extra-small mt-1">
                              <i className="simple-icon-info mr-1" />
                              Nguyên liệu này đang ngừng kinh doanh. Hãy chọn nguyên liệu khác thay thế.
                            </div>
                          )}
                        </FormGroup>
                      </Colxx>

                    {/* 2. Số lượng sử dụng (3) */}
                    <Colxx
                      xxs="7"
                      sm="2"
                      md="2"
                      className="has-float-label pr-0 pl-3 pl-sm-0"
                    >
                      <FormGroup>
                        <Label className="text-muted text-small">
                          {intl.formatMessage({ id: "recipe.label.usage" })}
                          {ing.unit ? ` (${ing.unit})` : ""}
                        </Label>
                        <Input
                          type="text"
                          placeholder="Nhập số lượng sử dụng"
                          value={formatNumber(ing.quantity)}
                          onChange={(e) =>
                            handleQuantityInfoChange(
                              ing.id,
                              "quantity",
                              e.target.value,
                            )
                          }
                          disabled={disabled}
                        />
                      </FormGroup>
                    </Colxx>

                    {/* 3. Thành tiền (3) */}
                    <Colxx xxs="5" sm="4" md="3">
                      <FormGroup>
                        <Label className="text-muted text-small d-block">
                          {intl.formatMessage({ id: "recipe.label.total" })}
                        </Label>
                        <div className="font-weight-bold text-primary mt-2">
                          {formatNumber(
                            Math.round(
                              (ing.price || 0) *
                                (parseFloat(ing.quantity) || 0),
                            ),
                          )}{" "}
                          đ
                        </div>
                      </FormGroup>
                    </Colxx>

                    {/* 4. Xóa (2) */}
                    <Colxx xxs="12" sm="1" md="1" className="text-right pl-0">
                      {!disabled && (
                        <Button
                          color="link"
                          className="text-danger p-0 mt-1"
                          onClick={() => handleRemoveIngredient(ing.id)}
                          title={intl.formatMessage({
                            id: "recipe.btn.delete-tooltip",
                          })}
                          style={{ minWidth: "30px" }}
                        >
                          <i
                            className="simple-icon-trash"
                            style={{ fontSize: "18px" }}
                          />
                          <span className="d-sm-none ml-2">
                            {intl.formatMessage({ id: "recipe.btn.delete" })}
                          </span>
                        </Button>
                      )}
                    </Colxx>
                  </Row>

                  {/* HÀNG 2: Nếu là NEW STOCK -> 5 Inputs */}
                  {ing.is_new_stock && (
                    <div className="mt-2 mb-3">
                      <Label
                        className="font-weight-bold text-small mb-3 text-primary"
                        style={{ textDecoration: "underline" }}
                      >
                        <i className="simple-icon-plus mr-1"></i>
                        {intl.formatMessage({ id: "recipe.new-stock.title" })}
                      </Label>
                      <Row className="no-gutters">
                        {/* Đơn vị tính */}
                        <Colxx
                          xxs="6"
                          sm="4"
                          className="has-float-label pr-1 pr-sm-2"
                        >
                          <FormGroup>
                            <Label className="text-muted text-small">
                              {intl.formatMessage({ id: "recipe.label.unit" })}
                            </Label>
                            <Input
                              value={ing.unit || ""}
                              onChange={(e) =>
                                handleQuantityInfoChange(
                                  ing.id,
                                  "unit",
                                  e.target.value,
                                )
                              }
                              bsSize="sm"
                              placeholder={unitPlaceholder}
                            />
                          </FormGroup>
                        </Colxx>
                        {/* SL Nhập */}
                        <Colxx
                          xxs="6"
                          sm="4"
                          className="has-float-label pl-1 px-sm-2"
                        >
                          <FormGroup>
                            <Label className="text-muted text-small">
                              {intl.formatMessage({
                                id: "recipe.label.import-qty",
                              })}
                            </Label>
                            <PriceInput
                              value={ing.stock_quantity}
                              placeholder="Nhập số lượng nhập"
                              onChange={(val) =>
                                handleQuantityInfoChange(
                                  ing.id,
                                  "stock_quantity",
                                  val,
                                )
                              }
                              bsSize="sm"
                            />
                          </FormGroup>
                        </Colxx>
                        {/* Giá Vốn */}
                        <Colxx
                          xxs="6"
                          sm="4"
                          className="has-float-label pr-1 pl-sm-2"
                        >
                          <FormGroup>
                            <Label className="text-muted text-small">
                              {intl.formatMessage({
                                id: "recipe.label.cost-price",
                              })}
                              {ing.unit
                                ? ` / ${ing.unit}`
                                : intl.formatMessage({
                                    id: "recipe.unit.per-unit",
                                  })}
                            </Label>
                            <PriceInput
                              value={ing.stock_cost_price}
                              placeholder="Nhập giá vốn"
                              onChange={(val) =>
                                handleQuantityInfoChange(
                                  ing.id,
                                  "stock_cost_price",
                                  val,
                                )
                              }
                              bsSize="sm"
                            />
                          </FormGroup>
                        </Colxx>
                        {/* Đã Trả */}
                        <Colxx
                          xxs="6"
                          sm="4"
                          className="has-float-label pl-1 pr-sm-2"
                        >
                          <FormGroup>
                            <Label className="text-muted text-small">
                              {intl.formatMessage({ id: "recipe.label.paid" })}
                            </Label>
                            <PriceInput
                              value={ing.stock_paid}
                              placeholder="Nhập số tiền đã trả"
                              onChange={(val) =>
                                handleQuantityInfoChange(
                                  ing.id,
                                  "stock_paid",
                                  val,
                                )
                              }
                              bsSize="sm"
                            />
                          </FormGroup>
                        </Colxx>

                        {/* Nhà Cung Cấp */}
                        <Colxx
                          xxs="12"
                          sm="5"
                          className="has-float-label mt-2 mt-sm-0 px-sm-2"
                        >
                          <FormGroup>
                            <Label className="text-muted text-small">
                              {intl.formatMessage({
                                id: "recipe.label.provider",
                              })}
                            </Label>
                            <Creatable
                              className="react-select"
                              classNamePrefix="react-select"
                              placeholder="Chọn hoặc tạo nhà cung cấp"
                              options={providerOptions}
                              value={getSelectedProvider(ing.stock_supplier)}
                              onChange={(opt) =>
                                handleQuantityInfoChange(
                                  ing.id,
                                  "stock_supplier",
                                  opt ? opt.label : "",
                                )
                              }
                              formatCreateLabel={(val) =>
                                intl.formatMessage(
                                  { id: "recipe.create-provider" },
                                  { val },
                                )
                              }
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                control: (base) => ({
                                  ...base,
                                  minHeight: "31px",
                                  height: "31px",
                                }),
                              }}
                            />
                          </FormGroup>
                        </Colxx>

                        {/* Còn lại (Tính toán) */}
                        <Colxx
                          xxs="12"
                          sm="3"
                          className="text-right d-flex align-items-center justify-content-end mt-2 mt-sm-0 pl-sm-2"
                        >
                          <div>
                            <Label className="text-muted text-small d-block mb-0">
                              {intl.formatMessage({ id: "recipe.label.debt" })}
                            </Label>
                            <div
                              className={`font-weight-bold mt-1 ${ing.stock_cost_price * ing.stock_quantity - (ing.stock_paid || 0) > 0 ? "text-danger" : "text-success"}`}
                              style={{ fontSize: "11px" }}
                            >
                              {AddCommaNumber(
                                parseFloat(ing.stock_cost_price || 0) *
                                  parseFloat(ing.stock_quantity || 0) -
                                  parseFloat(ing.stock_paid || 0),
                              )}
                            </div>
                          </div>
                        </Colxx>
                      </Row>
                    </div>
                  )}
                </div>
              );
            })
          )}

            {!disabled && (
              <div className="text-start pb-3 mb-3 border-bottom">
                <Button
                  color="outline-primary"
                  size="sm"
                  onClick={handleAddIngredient}
                >
                  <i className="simple-icon-plus mr-1" />{" "}
                  {intl.formatMessage({ id: "recipe.btn.add" })}
                </Button>
              </div>
            )}

            {/* Footer Calculation */}
            {localIngredients.length > 0 && (
              <div className="d-flex justify-content-between mb-3 pt-2">
                <div className="text-left">
                  <span className="text-muted text-small mr-2">
                    {intl.formatMessage({ id: "recipe.footer.total-cost" })}
                  </span>
                  <span
                    className="font-weight-bold text-success"
                    style={{ fontSize: "14px" }}
                  >
                    {AddCommaNumber(Math.round(totalCost))}đ
                  </span>
                </div>
                {sellingPrice > 0 && totalCost > 0 && (
                  <div className="text-right">
                    <span className="text-muted text-small mr-2">
                      {intl.formatMessage({
                        id: "recipe.footer.expected-profit",
                      })}
                    </span>
                    <span
                      className={`font-weight-bold ${sellingPrice - totalCost >= 0 ? "text-success" : "text-danger"}`}
                    >
                      {(
                        ((sellingPrice - totalCost) / sellingPrice) *
                        100
                      ).toFixed(1)}
                      % ({AddCommaNumber(Math.round(sellingPrice - totalCost))}
                      đ)
                    </span>
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </Collapse>
    </div>
  );
};

export default injectIntl(RecipeBuilder);
